import { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { BotInfo, WSOutgoingMessage } from '../../types';
import { GroupAvatar } from '../shared/GroupAvatar';
import { BotAvatar } from '../shared/BotAvatar';
import { useT } from '../../utils/i18n';
import styles from './CreateGroupModal.module.css';

const GROUP_COLORS = ['#5a6a2a', '#e34a2a', '#5a8ab8', '#c79a3a', '#8a6ac8', '#7ec9a8', '#3a3a2e', '#b85a5a'];

interface CreateGroupModalProps {
  bots: BotInfo[];
  send: (msg: WSOutgoingMessage) => void;
  onClose: () => void;
}

export function CreateGroupModal({ bots, send, onClose }: CreateGroupModalProps) {
  const t = useT();
  const [name, setName] = useState('');
  const [color, setColor] = useState(GROUP_COLORS[0]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const overlayRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const toggle = useCallback((botName: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(botName)) next.delete(botName); else next.add(botName);
      return next;
    });
  }, []);

  const handleCreate = useCallback(() => {
    const members = Array.from(selected);
    if (members.length < 2 || !name.trim()) return;
    send({ type: 'create_group', name: name.trim(), members });
    // Save group color to localStorage keyed by name
    try { localStorage.setItem(`novax:groupColor:${name.trim()}`, color); } catch {}
    onClose();
  }, [name, color, selected, send, onClose]);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  }, [onClose]);

  const canCreate = name.trim().length > 0 && selected.size >= 2;

  return createPortal(
    <div className={styles.overlay} ref={overlayRef} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h3 className={styles.title}>{t('chat.createGroup')}</h3>
          <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>

        <div className={styles.body}>
          {/* Avatar preview + color picker */}
          <div className={styles.avatarSection}>
            <GroupAvatar color={color} size={64} />
            <div className={styles.colorPicker}>
              {GROUP_COLORS.map((c) => (
                <button
                  key={c}
                  className={`${styles.colorDot} ${c === color ? styles.colorDotActive : ''}`}
                  style={{ background: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>

          {/* Group name */}
          <label className={styles.label}>{t('chat.groupNameLabel')}</label>
          <input
            ref={inputRef}
            className={styles.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('chat.groupNamePlaceholder')}
            maxLength={50}
            onKeyDown={(e) => { if (e.key === 'Enter' && canCreate) handleCreate(); }}
          />

          {/* Member selection */}
          <label className={styles.label}>
            {t('chat.selectMembers')}
            <span className={styles.countBadge}>{selected.size} / {bots.length}</span>
          </label>
          <div className={styles.memberList}>
            {bots.map((bot) => {
              const checked = selected.has(bot.name);
              return (
                <button
                  key={bot.name}
                  className={`${styles.memberRow} ${checked ? styles.memberRowChecked : ''}`}
                  onClick={() => toggle(bot.name)}
                >
                  <BotAvatar name={bot.name} size="sm" />
                  <div className={styles.memberInfo}>
                    <span className={styles.memberName}>{bot.name}</span>
                    {bot.description && <span className={styles.memberDesc}>{bot.description}</span>}
                  </div>
                  <div className={`${styles.checkbox} ${checked ? styles.checkboxChecked : ''}`}>
                    {checked && (
                      <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {selected.size > 0 && selected.size < 2 && (
            <p className={styles.hint}>{t('chat.minMembers')}</p>
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose}>{t('create.cancel')}</button>
          <button className={styles.createBtn} onClick={handleCreate} disabled={!canCreate}>
            {t('chat.createGroup')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
